import {DataStatus, Separator, Table} from 'platform/components';
import {Box, Hide, VStack} from 'platform/foundation';

import {useState} from 'react';

import {head, length} from 'ramda';

import {DrillDownLevelName, useSourcingDrillDownQuery} from '@dms/api/core';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {DrillDownHoverSurface} from './components/DrillDownHoverSurface';
import {DrillDownLevelsConfigurator} from './components/DrillDownLevelsConfigurator';
import {DrillDownRow, DrillDownRowProps} from './components/DrillDownRow';
import {
  getConfigurationForConfigurator,
  getConfigurationFromConfigurator,
} from './utils/getConfigurationForConfigurator';
import {getKeyFromLevels} from './utils/getKeyFromLevels';
import {getLevelConfigurations} from './utils/getLevelConfigurations';
import {getPermanentLevelFilters} from './utils/getPermanentLevelFilters';
import {getTableColumns} from './utils/getTableColumns';
import {sortOptionalLevelRows} from './utils/sortOptionalLevelRows';

export interface DrillDownProps extends TestIdProps {
  currency: string;
  regions: string[];
  permanentFilters?: Partial<Record<DrillDownLevelName, string>>;
  hiddenLevels?: DrillDownLevelName[];
  selectedRow: Record<DrillDownLevelName, string | Nullish>;
  scrollToSelectedRow?: boolean;
  hideConfigurator?: boolean;
  hasUnifiedSurfaces?: boolean;
  /**
   * Called per row with its path of levels. Returns a loader of the row's ad server URL,
   * which makes the Stock (pcs) value open that URL in a new tab, or `null` to keep it plain.
   */
  getStockUrlLoader?: DrillDownRowProps['getStockUrlLoader'];
}

export function DrillDown(props: DrillDownProps) {
  const [levelConfigurations, setLevelConfigurations] = useState(() =>
    getLevelConfigurations(props.hiddenLevels)
  );

  const permanentLevelFilters = getPermanentLevelFilters(props.permanentFilters);
  const {data, isLoading, isFetching, isError} = useSourcingDrillDownQuery({
    levels: [
      ...permanentLevelFilters,
      {
        level: head(levelConfigurations)?.name ?? 'make',
        values: [],
        priority: length(permanentLevelFilters) + 1,
      },
    ],
    currency: props.currency,
    regions: props.regions,
  });

  const columns = getTableColumns(props.currency);

  return (
    <VStack spacing={3}>
      <Hide when={props.hideConfigurator}>
        <DrillDownLevelsConfigurator
          value={getConfigurationForConfigurator(levelConfigurations)}
          onChange={(value) =>
            setLevelConfigurations(getConfigurationFromConfigurator(value, props.hiddenLevels))
          }
        />
      </Hide>
      <DataStatus isLoading={isLoading || isFetching} isError={isError} minHeight={50}>
        <Box minWidth={200} paddingBottom={1}>
          <Hide when={props.hideConfigurator}>
            <Separator orientation="horizontal" spacing={0} />
          </Hide>
          {/* TODO(T20-141285): Remove this prop and the cell/surface branches with SOURCING_UNIFIED_SURFACES. */}
          <DrillDownHoverSurface isEnabled={props.hasUnifiedSurfaces ?? false}>
            <Table
              variant="stripped"
              columns={columns}
              stickyHeader
              data-testid={suffixTestId('drillDown', props)}
            >
              {sortOptionalLevelRows(data?.statisticsAggregated ?? []).map((item) => (
                <DrillDownRow
                  key={getKeyFromLevels(item.levels)}
                  depth={0}
                  data={item}
                  levelConfigurations={levelConfigurations}
                  currency={props.currency}
                  translations={data?.translations ?? {}}
                  regions={props.regions}
                  selectedRow={props.selectedRow}
                  scrollToSelectedRow={props.scrollToSelectedRow}
                  hasUnifiedSurfaces={props.hasUnifiedSurfaces}
                  getStockUrlLoader={props.getStockUrlLoader}
                />
              ))}
            </Table>
          </DrillDownHoverSurface>
        </Box>
      </DataStatus>
    </VStack>
  );
}
